import {
  offlineLocalSessionsAtom,
  offlineSyncQueueAtom,
  readOfflinePendingServerDeleteIds,
} from '@/atoms/offline-recording';
import type { SessionScope } from '@heidi/schema/ml-scribe';
import {
  newSessionListSortByAtom,
  newSessionListSortOrderAtom,
  newSessionListViewByScopeAtom,
  pickCachedRestoreSessionId,
} from '@heidi/session';
import {
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  getCachedDefaultSessionsListWindows,
  isUrgentOnlyFilterAtom,
  selectedClinicianIdsFilterAtom,
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
  sessionListScopeAtom,
  sessionListSearchQueryAtom,
} from '@heidi/session/data';
import { useAtomValue } from 'jotai';

/**
 * True when the list will query its default key set: no filters, no search,
 * default sort. Both the runtime atoms and the per-scope persisted view are
 * checked — the persisted view is hydrated into the runtime atoms when the
 * list mounts, so a filter waiting in storage would change the key the list
 * actually asks for.
 */
function useIsDefaultSessionsListView(scope: SessionScope): boolean {
  const selectedStatuses = useAtomValue(selectedStatusesFilterAtom);
  const selectedDateRange = useAtomValue(selectedDateRangeFilterAtom);
  const selectedPatientProfiles = useAtomValue(selectedPatientProfilesFilterAtom);
  const selectedClinicianIds = useAtomValue(selectedClinicianIdsFilterAtom);
  const isUrgentOnly = useAtomValue(isUrgentOnlyFilterAtom);
  const searchQuery = useAtomValue(sessionListSearchQueryAtom);
  const sortBy = useAtomValue(newSessionListSortByAtom);
  const sortOrder = useAtomValue(newSessionListSortOrderAtom);
  const viewByScope = useAtomValue(newSessionListViewByScopeAtom);

  const persistedView = viewByScope.byScope?.[scope];
  const persistedClinicianIds = viewByScope.clinicianIdsByScope?.[scope];

  return (
    selectedStatuses.length === 0 &&
    !selectedDateRange.from &&
    !selectedDateRange.to &&
    selectedPatientProfiles.length === 0 &&
    selectedClinicianIds.length === 0 &&
    !isUrgentOnly &&
    searchQuery === '' &&
    sortBy === DEFAULT_SORT_BY &&
    sortOrder === DEFAULT_SORT_ORDER &&
    (persistedView?.sortBy ?? DEFAULT_SORT_BY) === DEFAULT_SORT_BY &&
    (persistedView?.sortOrder ?? DEFAULT_SORT_ORDER) === DEFAULT_SORT_ORDER &&
    !persistedView?.statuses?.length &&
    !persistedView?.dateRange?.from &&
    !persistedView?.dateRange?.to &&
    !persistedView?.isUrgentOnly &&
    !persistedClinicianIds?.length
  );
}

/**
 * Session id a surface outside the list can navigate straight to, matching
 * what the list's restore would select from the rows it has already cached.
 * Undefined whenever that cannot be established simply — a non-default view,
 * offline rows in play, nothing cached, or any Upcoming row (see
 * {@link pickCachedRestoreSessionId}) — so callers keep their fallback route.
 *
 * Reads the cache point-in-time at render and never subscribes to it: no
 * request, no list observer, no polling.
 */
export function useCachedRestoreTargetSessionId(): string | undefined {
  const scope = useAtomValue(sessionListScopeAtom);
  const isDefaultView = useIsDefaultSessionsListView(scope);
  // A pre-sync recording is unioned into the rendered rows from these atoms,
  // ahead of anything the server sent, so the cached rows alone are not the
  // list restore reads. Offline is an explicit fallback case.
  const offlineSyncQueue = useAtomValue(offlineSyncQueueAtom);
  const offlineLocalSessions = useAtomValue(offlineLocalSessionsAtom);

  if (!isDefaultView) return undefined;
  if (offlineSyncQueue.length > 0 || offlineLocalSessions.length > 0) return undefined;

  return pickCachedRestoreSessionId({
    windows: getCachedDefaultSessionsListWindows(scope),
    now: new Date(),
    pendingServerDeleteIds: readOfflinePendingServerDeleteIds(),
  });
}
